import React, { useState } from 'react';
import { Send, CheckCircle, Home, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { leadsAPI } from '../../services/api';
import { trackLeadSubmission } from '../../utils/googleAds';

const SellerLeadForm = ({ title = 'Get Your Free Home Evaluation', subtitle = "Find out what your home is worth in today's market" }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Property Details
    propertyAddress: '',
    propertyCity: '',
    propertyPostalCode: '',
    bedrooms: '',
    bathrooms: '',
    homeAge: '',
    renovationLevel: '',
    garage: '',
    squareFootage: '',
    homeStyle: '',
    additionalInfo: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consentChecked) {
      setError('Please agree to the Terms of Use and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    
    try {
      // Build a detailed message with all property info
      const propertyDetails = `
PROPERTY DETAILS:
Address: ${formData.propertyAddress}, ${formData.propertyCity}, ${formData.propertyPostalCode}
Bedrooms: ${formData.bedrooms}
Bathrooms: ${formData.bathrooms}
Home Age: ${formData.homeAge}
Renovation Level: ${formData.renovationLevel}
Garage: ${formData.garage}
Square Footage: ${formData.squareFootage}
Home Style: ${formData.homeStyle}

Additional Info: ${formData.additionalInfo || 'None provided'}
      `.trim();

      await leadsAPI.create({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: propertyDetails,
        source: 'seller_evaluation',
        // Include property details for KVcore
        property_address: formData.propertyAddress,
        property_city: formData.propertyCity,
      });
      setSubmitted(true);
      // Track Google Ads conversion
      trackLeadSubmission('seller_evaluation');
    } catch (err) {
      setError('Failed to submit. Please try again.');
      console.error('Lead submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-8 text-center">
          <CheckCircle className="mx-auto mb-4 text-green-600" size={48} />
          <h3 className="text-xl font-bold text-green-800 mb-2">Thank You!</h3>
          <p className="text-green-700">
            I've received your home evaluation request. Alexander will contact you within 24 hours to discuss your property.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-xl border-0">
      <CardHeader className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-t-lg">
        <CardTitle className="text-center">
          <h3 className="text-2xl font-bold">{title}</h3>
          <p className="text-gray-400 text-sm font-normal mt-1">{subtitle}</p>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg">
              {error}
            </div>
          )}
          
          {/* Contact Information Section */}
          <div className="pb-4 border-b">
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs">1</span>
              Your Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">First Name *</label>
                <Input
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  placeholder="John"
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Last Name *</label>
                <Input
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  placeholder="Smith"
                  className="h-11"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Email *</label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@example.com"
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Phone *</label>
                <Input
                  required
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(226) 555-1234"
                  className="h-11"
                />
              </div>
            </div>
          </div>

          {/* Property Address Section */}
          <div className="pb-4 border-b">
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs">2</span>
              <MapPin size={16} className="text-red-600" />
              Property Address
            </h4>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Street Address *</label>
              <Input
                required
                value={formData.propertyAddress}
                onChange={(e) => setFormData({...formData, propertyAddress: e.target.value})}
                placeholder="123 Main Street"
                className="h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">City *</label>
                <Input
                  required
                  value={formData.propertyCity}
                  onChange={(e) => setFormData({...formData, propertyCity: e.target.value})}
                  placeholder="Brantford"
                  className="h-11"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Postal Code</label>
                <Input
                  value={formData.propertyPostalCode}
                  onChange={(e) => setFormData({...formData, propertyPostalCode: e.target.value})}
                  placeholder="N3R 2H3"
                  className="h-11"
                />
              </div>
            </div>
          </div>

          {/* Property Details Section */}
          <div className="pb-4 border-b">
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs">3</span>
              <Home size={16} className="text-red-600" />
              Property Details
            </h4>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Bedrooms *</label>
                <Select 
                  value={formData.bedrooms} 
                  onValueChange={(v) => setFormData({...formData, bedrooms: v})}
                  required
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="6+">6+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Bathrooms *</label>
                <Select 
                  value={formData.bathrooms} 
                  onValueChange={(v) => setFormData({...formData, bathrooms: v})}
                  required
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="1.5">1.5</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="2.5">2.5</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="3.5">3.5</SelectItem>
                    <SelectItem value="4+">4+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Home Age</label>
                <Select 
                  value={formData.homeAge} 
                  onValueChange={(v) => setFormData({...formData, homeAge: v})}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New Construction</SelectItem>
                    <SelectItem value="0-5">0-5 years</SelectItem>
                    <SelectItem value="5-10">5-10 years</SelectItem>
                    <SelectItem value="10-20">10-20 years</SelectItem>
                    <SelectItem value="20-30">20-30 years</SelectItem>
                    <SelectItem value="30-50">30-50 years</SelectItem>
                    <SelectItem value="50+">50+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Garage</label>
                <Select 
                  value={formData.garage} 
                  onValueChange={(v) => setFormData({...formData, garage: v})}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Garage</SelectItem>
                    <SelectItem value="1-car">1 Car</SelectItem>
                    <SelectItem value="2-car">2 Car</SelectItem>
                    <SelectItem value="3-car">3+ Car</SelectItem>
                    <SelectItem value="carport">Carport</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Square Footage</label>
                <Select 
                  value={formData.squareFootage} 
                  onValueChange={(v) => setFormData({...formData, squareFootage: v})}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-1000">Under 1,000 sq ft</SelectItem>
                    <SelectItem value="1000-1500">1,000 - 1,500 sq ft</SelectItem>
                    <SelectItem value="1500-2000">1,500 - 2,000 sq ft</SelectItem>
                    <SelectItem value="2000-2500">2,000 - 2,500 sq ft</SelectItem>
                    <SelectItem value="2500-3000">2,500 - 3,000 sq ft</SelectItem>
                    <SelectItem value="3000-4000">3,000 - 4,000 sq ft</SelectItem>
                    <SelectItem value="4000+">4,000+ sq ft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Home Style</label>
                <Select 
                  value={formData.homeStyle} 
                  onValueChange={(v) => setFormData({...formData, homeStyle: v})}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bungalow">Bungalow</SelectItem>
                    <SelectItem value="2-storey">2 Storey</SelectItem>
                    <SelectItem value="split-level">Split Level</SelectItem>
                    <SelectItem value="bi-level">Bi-Level</SelectItem>
                    <SelectItem value="1.5-storey">1.5 Storey</SelectItem>
                    <SelectItem value="multi-level">Multi-Level</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                    <SelectItem value="condo">Condo/Apartment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Level of Renovations</label>
              <Select 
                value={formData.renovationLevel} 
                onValueChange={(v) => setFormData({...formData, renovationLevel: v})}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select renovation level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="original">Original Condition (No Renovations)</SelectItem>
                  <SelectItem value="minor">Minor Updates (Paint, Fixtures)</SelectItem>
                  <SelectItem value="moderate">Moderate Renovations (Kitchen/Bath Updates)</SelectItem>
                  <SelectItem value="major">Major Renovations (Full Remodel)</SelectItem>
                  <SelectItem value="fully-renovated">Fully Renovated / Move-In Ready</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Additional Information</label>
            <Textarea
              value={formData.additionalInfo}
              onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
              placeholder="Tell me about any special features, recent upgrades, or anything else I should know about your property..."
              rows={4}
            />
          </div>

          <div className="flex items-start gap-2 pt-2">
            <input
              data-testid="seller-form-consent"
              id="sellerform-consent"
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
            />
            <label htmlFor="sellerform-consent" className="text-xs text-gray-600 cursor-pointer leading-relaxed">
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">Terms of Use</a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">Privacy Policy</a>,
              and consent to receiving a home evaluation and real estate updates from Alexander Conti.
            </label>
          </div>

          <Button 
            type="submit" 
            disabled={loading || !consentChecked}
            className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send size={18} />
                Request Free Evaluation
              </span>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            Your information is secure and will never be shared with third parties outside our brokerage.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default SellerLeadForm;
